<script lang="ts">
import VRuntimeTemplate from 'vue3-runtime-template'
import ListDeleteConfirmation from './deleteConfirmation.vue'

export default {
    name: 'ListTable',
    components: {
        ListDeleteConfirmation,
        VRuntimeTemplate,
    },
    props: {
        showGlobalLoader: { type: Boolean, required: false },
        highlightRow: { type: Array, required: false, default: () => [] },
        editPath: { type: String, required: false, default: '' },
        disableView: { type: Boolean, required: false },
        permissionSkip: { type: Boolean, required: false },
        permissionToCheck: { type: String, required: false, default: '' },
        columns: { type: Array, required: true },
        translationName: { type: String, required: true },
        sortable: { type: Array, required: true },
        valueAsViewLink: { type: Array, required: false, default: () => [] },
        defaultUrl: { type: String, required: false, default: '' },
        initSort: { type: Object, required: false, default: null },
        isSelectable: { type: Boolean, required: false },
        dblClickColumns: { type: Array, required: false, default: () => [] },
        clickColumns: { type: Array, required: false, default: () => [] },
        noAction: { type: Boolean, required: false },
        customHeight: { type: Number, required: false, default: null },
        permissionName: { type: String, required: true },
        density: { type: String, required: false, default: 'comfortable' },
        data: {
            type: Array,
            required: true,
        },
        loading: { type: Boolean },

    },
    emits: ['deleteData', 'restoreData', 'dblClickColumn', 'clickColumn', 'resetPagination', 'loadData', 'defaultRedirectClicked', 'updateSort', 'updateRow'],
    data() {
        return {
            // sort : {...this.initSort},
            sort: this.initSort ? { ...this.initSort } : { key: this.columns[0]?.dataKey, order: 'asc' },
            selectedData: [],
        }
    },
    computed: {
        showOptions() {
            return this.$permissionCheck(`${this.permissionName}_restore`) || this.$permissionCheck(`${this.permissionName}_destroy`) || this.$permissionCheck(`${this.permissionName}_pdf`) || this.$permissionCheck(`${this.permissionName}_update`) || this.$permissionCheck(`${this.permissionName}_show`)
        },
        sortArray() {
            return this.sortable.map(s => s.name)
        },
        updatedColumns() {
            // HANDLE DISABLED_FIELD IN .ENV
            if (this.data && this.data.length) {
                return this.columns.filter((col) => {
                    let allowed = true

                    // FILTER : IS PERMISSION ALLOWED ?
                    const permission = col.permission
                    if (permission) {
                        allowed = this.$permissionCheck(permission)
                    }

                    // FILTER : DO KEY EXIST IN DATA ?
                    if (allowed) {
                        const keys = col.dataKey.split('.')
                        let value = this.data[0]
                        keys.forEach((k) => {
                            value = value ? value[k] : value
                        })
                        return value !== undefined
                    }
                    else {
                        return false
                    }
                })
            }
            return []
        },
    },
    methods: {
        getColumnHeaderTranslation(column: object) {
            if (column.translation) {
                return column.translation
            }
            return this.$translate(`${this.translationName}.${column.dataKey.split(' || ')[0]}`)
        },
        resetSelectedData() {
            this.selectedData = []
        },
        selectSingle(item, column) {
            if (this.dblClickColumns.includes(column.dataKey) && this.$permissionCheck(`${this.permissionName}_update`)) {
                this.resetSelectedData()
                this.selectedData.push(item)
                this.$emit('dblClickColumn', column.dataKey)
            }
            if (this.clickColumns.includes(column.dataKey)) {
                this.$emit('clickColumn', { column: column.dataKey, item })
            }
        },
        selectAll(checked) {
            this.selectedData = checked ? this.data.filter(item => item.meta ? item.meta.editable : true) : []
        },
        updateSort(key) {
            if (this.sortArray.includes(key)) {
                this.sort = this.sort.key === key
                    ? {
                            ...this.sort,
                            order: this.sort.order === 'asc' ? 'desc' : 'asc',
                        }
                    : { key, order: 'asc' }
                this.$emit('updateSort', this.sort)
                this.$emit('resetPagination')
                this.$emit('loadData')
            }
        },
        formatData(column, item) {
            const optionsKeys = column.dataKey.split(' || ')

            let value = item

            if (optionsKeys.length === 1) {
                const keys = column.dataKey.split('.')
                keys.forEach(k => value = value ? value[k] : value)
            }
            else if (optionsKeys.length > 1) { // HANDLE COLUMNS MULTIPLE OPTIONS -> KEEP THE NON NULL
                optionsKeys.forEach((key) => {
                    if (!value || value === item) {
                        const keys = key.split('.')
                        value = item
                        keys.forEach((k) => {
                            value = value ? value[k] : value
                        })
                    }
                })
            }

            let template = '{{ temporaryKey }}'

            if (column.template && column.template.length) { // Template b4 transform because if value changes, we can't use translation
                column.template.forEach((f) => {
                    template = f(column.dataKey, value, template, item)
                })
            }

            if (column.transform && column.transform.length) {
                column.transform.forEach((f) => {
                    value = f(column.dataKey, value, item) // TODO reverse values to f(value, column.dataKey)  because dataKey is facultative
                })
            }

            // HANDLE IF VALUE IS ARRAY AND SETUP WITH MULTIPLE TEMPLATE TO REPLACE ALL TEMPORARYKEY INTO REAL VALUE
            const totaltemporaryKey = (template.match(/\{\{ temporaryKey \}\}/g) || [])
            if (value && Array.isArray(value) && totaltemporaryKey.length === value.length) {
                value.forEach((val) => {
                    let t = 0
                    template = template.replace(/\{\{ temporaryKey \}\}/g, match => ++t === 1 ? val : match)
                })
                return template
            }

            // try to get data from an object
            if (typeof value === 'object') {
                for (const k of ['t', 'title', 'name', 'v']) {
                    if (value && value[k]) {
                        return value[k]
                    }
                }
            }

            // try to get data from a list of objects
            if (Array.isArray(value) && value?.length) {
                const result = []
                for (const object of value) {
                    if (typeof object === 'object') {
                        for (const k of ['t', 'title', 'name', 'v']) {
                            if (object && object[k]) {
                                result.push(object[k])
                            }
                        }
                    }
                }
                if (result?.length) {
                    value = result.join(', ')
                }
            }
            return value || typeof value === 'number' || value === false ? template.replace('{{ temporaryKey }}', value) : ''
        },
    },
}
</script>

<template>
    <!-- CLASSES BELOW ARE USEFULL FOR CSS -->
    <div class="position-relative library-table" :class="noAction ? 'without-actions' : 'with-actions'">
        <v-table
            v-if="permissionSkip || $permissionCheck(`${permissionName}_index`) || $permissionCheck(permissionToCheck)"
            class="default-table"
            :density="density"
            :fixed-header="data?.length > 10"
            :height="data?.length > 10 ? customHeight || '' : ''"
        >
            <thead>
                <tr v-if="!updatedColumns.length && loading">
                    <v-skeleton-loader type="table-thead" />
                </tr>
                <tr v-if="!updatedColumns.length" />
                <tr v-else>
                    <th
                        v-if="isSelectable && data.filter(item => item.meta?.editable).length"
                        class="isSelectableHeader px-2 pl-1"
                    >
                        <v-checkbox
                            class="select-data-table"
                            color="primary"
                            hide-details
                            density="compact"
                            @update:model-value="selectAll($event)"
                        />
                    </th>

                    <th
                        v-for="(column, i) in updatedColumns"
                        :key="i"
                        class="text-left px-2 pr-6 position-relative"
                        @click.prevent="updateSort(column.dataKey)"
                    >
                        {{ getColumnHeaderTranslation(column) }}
                        <!-- SORT BY -->
                        <span
                            v-if="sortArray.includes(column.dataKey)"
                            class="sort-icon"
                        >
                            <v-icon
                                v-if="sort && column.dataKey === sort.key"
                                :icon="sort.order === 'asc' ? 'mdi-menu-down' : 'mdi-menu-up'"
                            />
                            <v-icon
                                v-else
                                icon="mdi-menu-swap"
                            />
                        </span>
                    </th>
                    <th v-if="$slots.customColumn" class="text-left px-2">
                        {{ translationName ? $translate(`${translationName}.custom_column`) : "" }}
                    </th>
                    <th
                        v-if="!noAction"
                        class="text-left px-2 border-b"
                    >
                    <!-- {{ $translate('actions') }} -->
                    </th>
                </tr>
            </thead>

            <tbody v-if="loading && !data.length">
                <tr
                    v-for="i in 15"
                    :key="i"
                >
                    <td colspan="100%">
                        <v-skeleton-loader type="table-row-divider" />
                    </td>
                </tr>
            </tbody>
            <tbody
                v-else-if="!data.length"
                class="pa-5"
            >
                <tr class="text-center">
                    <td
                        class="pa-10"
                        :colspan="updatedColumns.length + 1"
                    >
                        Aucune donnée
                    </td>
                </tr>
            </tbody>
            <tbody v-else>
                <tr
                    v-for="(item, i) in data"
                    :key="i"
                    :class="{ isSelected: selectedData.includes(item) || highlightRow.includes(item.k) }"
                >
                    <td
                        v-if="isSelectable && data.filter(item => item.meta?.editable).length"
                        class="pa-1"
                    >
                        <v-checkbox
                            v-if="item.meta?.editable || !item.meta"
                            v-model="selectedData"
                            class="select-data-table"
                            color="primary"
                            :value="item"
                            hide-details
                            density="compact"
                        />
                    </td>
                    <td
                        v-for="(column, ci) in updatedColumns"
                        :key="ci"
                        class="px-2"
                        @dblclick.prevent="selectSingle(item, column)"
                        @click="selectSingle(item, column)"
                    >
                        <span
                            v-if="valueAsViewLink?.includes(column.dataKey) && $permissionCheck(`${permissionName}_show`) && !item.deleted"
                        >
                            <NuxtLink :to="`${defaultUrl}/${item.k}`">
                                <span class="text-info" @click="$emit('defaultRedirectClicked', item)"><VRuntimeTemplate
                                    :template="formatData(column, item)"
                                /></span>
                            </NuxtLink>
                        </span>
                        <component
                            :is="{ ...column.component }" v-else-if="column.component" :item="item"
                            :translation-name="translationName" @update-row="$emit('updateRow', $event)"
                        />
                        <VRuntimeTemplate
                            v-else
                            :template="formatData(column, item)"
                        />
                    </td>
                    <td v-if="$slots.customColumn">
                        <slot
                            name="customColumn"
                            :item="item"
                        />
                    </td>
                    <td
                        v-if="!noAction"
                        class="actions pr-0 pl-1 text-right"
                    >
                        <span v-if="$permissionCheck(`${permissionName}_show`) && !item.deleted && !disableView">
                            <NuxtLink
                                :to="`${defaultUrl}/${item.k}`"
                                aria-label="Détails"
                            >
                                <v-btn
                                    class="mr-1"
                                    icon="mdi-magnify"
                                    aria-label="Détails"
                                    density="comfortable"
                                    variant="text"
                                    size="small"
                                />
                            </NuxtLink>
                        </span>
                        <span
                            v-if="$permissionCheck(`${permissionName}_update`)
                                && !item.deleted
                                && (item.meta?.editable || !item.meta)"
                        >
                            <NuxtLink
                                :to="editPath ? editPath : `${defaultUrl}/${item.k}/formulaire`"
                                aria-label="Modifier"
                            >
                                <v-btn
                                    class="mr-1"
                                    icon="mdi-square-edit-outline"
                                    aria-label="Modifier"
                                    density="comfortable"
                                    variant="text"
                                    size="x-small"
                                />
                            </NuxtLink>
                        </span>
                        <!-- MORE OPTIONS -->
                        <v-btn
                            v-if="showOptions"
                            :id="`menu-table-toggle-${item.id}`"
                            icon="mdi-dots-vertical"
                            aria-label="Autres actions"
                            density="comfortable"
                            variant="text"
                            size="small"
                        />
                        <v-menu
                            class="table-menu"
                            :activator="`#menu-table-toggle-${item.id}`"
                        >
                            <v-list
                                density="compact"
                                @click.prevent.stop
                            >
                                <!-- VIEW -->
                                <NuxtLink
                                    :to="`${defaultUrl}/${item.k}`"
                                    aria-label="Détails"
                                    class="link-as-list-item"
                                >
                                    <v-list-item
                                        v-if="$permissionCheck(`${permissionName}_show`) && !item.deleted && !disableView"
                                        density="compact"
                                    >
                                        <v-list-item-title class="text-caption">
                                            <v-icon
                                                icon="mdi-magnify"
                                                aria-label="Détails"
                                                size="small"
                                                class="mr-2"
                                                color="dark"
                                            />
                                            Voir le détail
                                        </v-list-item-title>
                                    </v-list-item>
                                </NuxtLink>

                                <!-- EDIT -->
                                <NuxtLink
                                    :to="editPath ? editPath : `${defaultUrl}/${item.k}/formulaire`"
                                    aria-label="Modifier"
                                    class="link-as-list-item"
                                >
                                    <v-list-item
                                        v-if="$permissionCheck(`${permissionName}_update`)
                                            && !item.deleted
                                            && (item.meta?.editable || !item.meta)"
                                        density="compact"
                                    >
                                        <v-list-item-title class="text-caption">
                                            <v-icon
                                                icon="mdi-square-edit-outline"
                                                aria-label="Modifier"
                                                size="small"
                                                class="mr-2"
                                                color="dark"
                                            />
                                            Modifier
                                        </v-list-item-title>
                                    </v-list-item>
                                </NuxtLink>
                                <slot
                                    name="optionsList"
                                    :item="item"
                                />

                                <!-- DELETE -->
                                <v-list-item
                                    v-if="item && $permissionCheck(`${permissionName}_destroy`) && !item.deleted && (!item.meta || item.meta.deletable)"
                                    density="compact"
                                    class="link-as-list-item"
                                >
                                    <ListDeleteConfirmation
                                        :item="item"
                                        :translation-name="translationName"
                                        :button="`<v-list-item-title class='text-caption text-danger' ><v-icon icon='mdi mdi-archive' size='small' class='mr-3' color='danger' aria-label='Supprimer'></v-icon>${$translate(`${translationName}.delete`)}</v-list-item-title>`"
                                        @delete-data="$emit('deleteData', $event)"
                                    />
                                </v-list-item>
                                <!-- RESTORE -->
                                <v-list-item
                                    v-if="$permissionCheck(`${permissionName}_restore`) && item.deleted"
                                    density="compact"
                                    @click="$emit('restoreData', item)"
                                >
                                    <v-list-item-title class="text-caption text-success">
                                        <v-icon
                                            icon="mdi-restore"
                                            size="small"
                                            class="mr-2"
                                            color="success"
                                        />
                                        {{ $translate(`${translationName}.restore`) }}
                                    </v-list-item-title>
                                </v-list-item>
                            </v-list>
                        </v-menu>
                    <!-- END OPTIONS -->
                    </td>
                </tr>
            </tbody>
        </v-table>
        <div v-if="loading && showGlobalLoader" id="overlay">
            <div id="text">
                <v-progress-circular
                    color="primary"
                    indeterminate
                    :size="47"
                    :width="6"
                />
            </div>
        </div>
    </div>
</template>

<style scoped>
#overlay {
    position: absolute;
    background-color: rgba(0, 0, 0, 0.2);
    bottom: 0;
    font-size: 50px;
    height: 100%;
    left: 0;
    margin: 0 5px;
    right: 0;
    top: 0;
    z-index: 500;
}
</style>
